import type { CloudAmount, CloudLayer, FlightCategory, TafChange, TafPeriod } from './weather'
import { KT_PER_MS } from './weather'

// Raw TAF text to periods with UTC instants. A TAF gives only day and hour,
// so each day is placed in the month nearest `reference`. Change groups
// keep only what they state.

export interface TafTemperature {
  kind: 'max' | 'min'
  temperatureC: number
  at: string
}

export interface DecodedTaf {
  station: string
  amendment: 'AMD' | 'COR' | null
  issuedAt: string | null
  validFrom: string
  validTo: string
  nil: boolean
  cancelled: boolean
  periods: TafPeriod[]
  temperatures: TafTemperature[]
  unrecognised: string[]
}

export type WeatherElement =
  | { kind: 'wind'; directionDeg: number | null; speedKt: number; gustKt: number | null }
  | { kind: 'visibility'; metres: number }
  | { kind: 'cavok' }
  | { kind: 'cloud'; layer: CloudLayer }
  | { kind: 'verticalVisibility'; feet: number | null }
  | { kind: 'weather'; code: string }
  | { kind: 'nsw' }

const WEATHER_CODE =
  /^(\+|-|VC)?(MI|PR|BC|DR|BL|SH|TS|FZ)?((?:DZ|RA|SN|SG|IC|PL|GR|GS|UP|BR|FG|FU|VA|DU|SA|HZ|PY|PO|SQ|FC|SS|DS)*)$/

const speedToKt = (value: number, unit: string) =>
  unit === 'MPS' ? Math.round(value * KT_PER_MS) : unit === 'KMH' ? Math.round(value / 1.852) : value

export function readWeatherElement(token: string): WeatherElement | null {
  let m: RegExpMatchArray | null
  if ((m = token.match(/^(\d{3}|VRB)(\d{2,3})(?:G(\d{2,3}))?(KT|MPS|KMH)$/))) {
    return {
      kind: 'wind',
      directionDeg: m[1] === 'VRB' ? null : Number(m[1]),
      speedKt: speedToKt(Number(m[2]), m[4]!),
      gustKt: m[3] ? speedToKt(Number(m[3]), m[4]!) : null,
    }
  }
  if (token === 'CAVOK') return { kind: 'cavok' }
  if ((m = token.match(/^(\d{4})(NDV|[NSEW]{1,2})?$/))) return { kind: 'visibility', metres: Number(m[1]) }
  // North American TAFs: P6SM, 3SM, 1/2SM.
  if ((m = token.match(/^(P)?(\d+)(?:\/(\d+))?SM$/))) {
    const miles = m[3] ? Number(m[2]) / Number(m[3]) : Number(m[2])
    return { kind: 'visibility', metres: m[1] ? 9999 : Math.min(9999, Math.round((miles * 1609.344) / 100) * 100) }
  }
  if ((m = token.match(/^(FEW|SCT|BKN|OVC)(\d{3}|\/\/\/)(CB|TCU|\/\/\/)?$/))) {
    return {
      kind: 'cloud',
      layer: {
        amount: m[1] as CloudAmount,
        baseFt: m[2] === '///' ? null : Number(m[2]) * 100,
        type: m[3] && m[3] !== '///' ? m[3] : null,
      },
    }
  }
  if ((m = token.match(/^VV(\d{3}|\/\/\/)$/))) {
    return { kind: 'verticalVisibility', feet: m[1] === '///' ? null : Number(m[1]) * 100 }
  }
  if (token === 'NSC' || token === 'NCD' || token === 'SKC' || token === 'CLR') {
    return { kind: 'cloud', layer: { amount: token, baseFt: null, type: null } }
  }
  if (token === 'NSW') return { kind: 'nsw' }
  // A phenomenon, or a descriptor that stands alone (TS, VCSH).
  if ((m = token.match(WEATHER_CODE)) && (m[3] || m[2] === 'TS' || (m[1] === 'VC' && m[2]))) return { kind: 'weather', code: token }
  return null
}

// "-SHRA" -> light, SH, [RA].
export interface WeatherCodeParts {
  intensity: 'light' | 'heavy' | 'vicinity' | null
  descriptor: string | null
  phenomena: string[]
}

export function weatherCodeParts(code: string): WeatherCodeParts | null {
  const m = code.match(WEATHER_CODE)
  if (!m) return null
  return {
    intensity: m[1] === '-' ? 'light' : m[1] === '+' ? 'heavy' : m[1] === 'VC' ? 'vicinity' : null,
    descriptor: m[2] ?? null,
    phenomena: m[3]?.match(/.{2}/g) ?? [],
  }
}

const DAY_MS = 86_400_000

// Hour 24 is the following midnight.
export function resolveTafTime(day: number, hour: number, minute: number, reference: Date): Date {
  const year = reference.getUTCFullYear()
  const month = reference.getUTCMonth()
  let best: Date | null = null
  for (const offset of [-1, 0, 1]) {
    const candidate = new Date(Date.UTC(year, month + offset, day, hour, minute))
    // Day 31 rolls over in a 30-day month.
    if (candidate.getUTCDate() !== day && hour !== 24) continue
    if (!best || Math.abs(candidate.getTime() - reference.getTime()) < Math.abs(best.getTime() - reference.getTime())) {
      best = candidate
    }
  }
  return best ?? new Date(reference.getTime() - DAY_MS)
}

const RANGE = /^(\d{2})(\d{2})\/(\d{2})(\d{2})$/

function readRange(token: string | undefined, reference: Date): [string, string] | null {
  const m = token?.match(RANGE)
  if (!m) return null
  const from = resolveTafTime(Number(m[1]), Number(m[2]), 0, reference)
  let to = resolveTafTime(Number(m[3]), Number(m[4]), 0, reference)
  // Across a month end: 3122/0106.
  if (to <= from) to = new Date(Date.UTC(to.getUTCFullYear(), to.getUTCMonth() + 1, Number(m[3]), Number(m[4])))
  return [from.toISOString(), to.toISOString()]
}

function emptyPeriod(from: string, to: string, change: TafChange | null, probability: number | null): TafPeriod {
  return {
    from,
    to,
    change,
    probability,
    becomingBy: change === 'BECMG' ? to : null,
    wind: null,
    visibilityM: null,
    verticalVisibilityFt: null,
    cavok: false,
    noSignificantWeather: false,
    clouds: [],
    weather: [],
  }
}

function apply(period: TafPeriod, element: WeatherElement) {
  switch (element.kind) {
    case 'wind':
      period.wind = { directionDeg: element.directionDeg, speedKt: element.speedKt, gustKt: element.gustKt }
      break
    case 'visibility':
      period.visibilityM = element.metres
      break
    case 'cavok':
      period.cavok = true
      period.visibilityM = 9999
      break
    case 'cloud':
      period.clouds.push(element.layer)
      break
    case 'verticalVisibility':
      period.verticalVisibilityFt = element.feet
      break
    case 'weather':
      period.weather.push(element.code)
      break
    case 'nsw':
      period.noSignificantWeather = true
      break
  }
}

export function decodeTaf(raw: string, reference: Date): DecodedTaf {
  const tokens = raw.trim().replace(/=$/, '').split(/\s+/).filter(Boolean)
  let i = 0
  if (tokens[i] === 'TAF') i++
  let amendment: DecodedTaf['amendment'] = null
  while (tokens[i] === 'AMD' || tokens[i] === 'COR' || tokens[i] === 'RTD') {
    if (tokens[i] !== 'RTD') amendment = tokens[i] as 'AMD' | 'COR'
    i++
  }
  const station = tokens[i++] ?? ''

  let issuedAt: string | null = null
  const issue = tokens[i]?.match(/^(\d{2})(\d{2})(\d{2})Z$/)
  if (issue) {
    issuedAt = resolveTafTime(Number(issue[1]), Number(issue[2]), Number(issue[3]), reference).toISOString()
    i++
  }
  // Anchor to the issue time: a TAF fetched early on the 1st was issued last month.
  const anchor = issuedAt ? new Date(issuedAt) : reference
  const validity = readRange(tokens[i], anchor)
  if (validity) i++
  const [validFrom, validTo] = validity ?? [issuedAt ?? reference.toISOString(), issuedAt ?? reference.toISOString()]

  const result: DecodedTaf = {
    station,
    amendment,
    issuedAt,
    validFrom,
    validTo,
    nil: false,
    cancelled: false,
    periods: [],
    temperatures: [],
    unrecognised: [],
  }

  // The base forecast and each FM group run until the next FM.
  let current = emptyPeriod(validFrom, validTo, null, null)
  let main = current
  result.periods.push(current)
  let probability: number | null = null

  for (; i < tokens.length; i++) {
    const token = tokens[i]!
    let m: RegExpMatchArray | null
    if (token === 'NIL') result.nil = true
    else if (token === 'CNL') result.cancelled = true
    else if (token === 'RMK') break
    else if ((m = token.match(/^PROB(\d{2})$/))) {
      probability = Number(m[1])
      if (tokens[i + 1] === 'TEMPO') continue
      const range = readRange(tokens[i + 1], anchor)
      if (range) {
        i++
        current = emptyPeriod(range[0], range[1], 'PROB', probability)
        result.periods.push(current)
      } else result.unrecognised.push(token)
      probability = null
    } else if (token === 'BECMG' || token === 'TEMPO') {
      const range = readRange(tokens[i + 1], anchor)
      if (range) {
        i++
        current = emptyPeriod(range[0], range[1], token, probability)
        result.periods.push(current)
      } else result.unrecognised.push(token)
      probability = null
    } else if ((m = token.match(/^FM(\d{2})(\d{2})(\d{2})$/))) {
      const from = resolveTafTime(Number(m[1]), Number(m[2]), Number(m[3]), anchor).toISOString()
      main.to = from
      current = emptyPeriod(from, validTo, 'FM', null)
      main = current
      result.periods.push(current)
    } else if ((m = token.match(/^T([XN])(M?)(\d{2})\/(\d{2})(\d{2})Z$/))) {
      result.temperatures.push({
        kind: m[1] === 'X' ? 'max' : 'min',
        temperatureC: (m[2] ? -1 : 1) * Number(m[3]),
        at: resolveTafTime(Number(m[4]), Number(m[5]), 0, anchor).toISOString(),
      })
    } else {
      const element = readWeatherElement(token)
      if (element) apply(current, element)
      else result.unrecognised.push(token)
    }
  }
  if (result.nil || result.cancelled) result.periods = []
  return result
}

const STATUTE_MILE_M = 1609.344

// FAA flight categories, as AviationWeather.gov reports for a METAR.
export function flightCategoryOf(visibilityM: number | null, ceilingFt: number | null): FlightCategory | null {
  if (visibilityM === null && ceilingFt === null) return null
  const vis = visibilityM ?? Infinity
  const ceiling = ceilingFt ?? Infinity
  if (ceiling < 500 || vis < STATUTE_MILE_M) return 'LIFR'
  if (ceiling < 1000 || vis < 3 * STATUTE_MILE_M) return 'IFR'
  if (ceiling <= 3000 || vis <= 5 * STATUTE_MILE_M) return 'MVFR'
  return 'VFR'
}

export function ceilingFt(clouds: CloudLayer[], verticalVisibilityFt: number | null = null): number | null {
  const bases = clouds
    .filter((c) => (c.amount === 'BKN' || c.amount === 'OVC' || c.amount === 'VV') && c.baseFt !== null)
    .map((c) => c.baseFt!)
  if (verticalVisibilityFt !== null) bases.push(verticalVisibilityFt)
  return bases.length > 0 ? Math.min(...bases) : null
}

// Null for a group that states neither visibility nor cloud.
export function periodFlightCategory(period: TafPeriod): FlightCategory | null {
  if (period.cavok) return 'VFR'
  const ceiling = ceilingFt(period.clouds, period.verticalVisibilityFt)
  const statesCloud = period.clouds.length > 0 || period.verticalVisibilityFt !== null
  if (period.visibilityM === null && !statesCloud) return null
  return flightCategoryOf(period.visibilityM, statesCloud ? (ceiling ?? Infinity) : null)
}

export const periodInForce = (period: TafPeriod, at: number) => Date.parse(period.from) <= at && at < Date.parse(period.to)
